import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/context/CurrencyContext";
import { paymentTotals } from "@/lib/payments";
import { Wallet, CheckCircle2, Clock, AlertTriangle } from "lucide-react";

// Literal Tailwind class strings (no runtime-built class names) so the JIT
// compiler always includes them.
const TILE_STYLE = {
  all: {
    icon: Wallet,
    iconCls: "text-[hsl(var(--primary))]",
    activeCls: "border-[hsl(var(--primary))] ring-1 ring-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.06)]",
    idleCls: "",
  },
  Paid: {
    icon: CheckCircle2,
    iconCls: "text-[hsl(var(--success))]",
    activeCls: "border-[hsl(var(--success))] ring-1 ring-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.06)]",
    idleCls: "",
  },
  Partial: {
    icon: Clock,
    iconCls: "text-[hsl(var(--warning))]",
    activeCls: "border-[hsl(var(--warning))] ring-1 ring-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.06)]",
    idleCls: "",
  },
  Unpaid: {
    icon: AlertTriangle,
    iconCls: "text-[hsl(var(--danger))]",
    activeCls: "border-[hsl(var(--danger))] ring-1 ring-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.06)]",
    idleCls: "border-[hsl(var(--danger)/0.35)] bg-[hsl(var(--danger)/0.04)]",
  },
};

const TILES = [
  { key: "all", label: "All" },
  { key: "Paid", label: "Paid" },
  { key: "Partial", label: "Partial" },
  { key: "Unpaid", label: "Unpaid" },
];

/**
 * PaymentStatusBar — a compact totals strip that doubles as a Paid / Partial /
 * Unpaid filter. Each tile shows the amount + invoice count for that bucket and
 * is clickable to filter. Unpaid is always visually emphasised so outstanding
 * bills stand out.
 */
export function PaymentStatusBar({ rows, value = "all", onChange, testId = "payment-status-bar" }) {
  const { money } = useCurrency();
  const totals = useMemo(() => paymentTotals(rows), [rows]);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={testId}>
      {TILES.map(({ key, label }) => {
        const style = TILE_STYLE[key];
        const Icon = style.icon;
        const active = value === key;
        const t = totals[key] || { amount: 0, count: 0 };
        const emphasize = key === "Unpaid" && t.amount > 0;
        return (
          <button
            type="button"
            key={key}
            onClick={() => onChange?.(key)}
            aria-pressed={active}
            data-testid={`${testId}-${key.toLowerCase()}`}
            className={cn(
              "flex flex-col items-start rounded-xl border bg-card p-3 text-left transition-colors duration-150",
              "hover:bg-[hsl(var(--surface-2))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
              active ? style.activeCls : emphasize ? style.idleCls : ""
            )}
          >
            <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <Icon className={cn("h-3.5 w-3.5", key !== "all" && style.iconCls)} /> {label}
            </span>
            <span className="mt-1 font-heading text-lg font-semibold tabular-nums" data-testid={`${testId}-${key.toLowerCase()}-amount`}>
              {money(t.amount)}
            </span>
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {t.count} invoice{t.count === 1 ? "" : "s"}
            </span>
          </button>
        );
      })}
    </div>
  );
}
